// Notifications controller

"use strict";

import { ApiNotifications } from "@/api/api-group-notifications";
import { Timeouts } from "@/utils/timeout";
import { Request } from "@asanrom/request-browser";
import { AppEvents } from "./app-events";
import { AuthController } from "./auth";
import type { NotificationItem } from "@/api/definitions";

const CHECK_NOTIFICATIONS_REQ_ID = "notifications-check";
const ALERT_NOTIFICATIONS_REQ_ID = "notifications-alert-check";
const DO_NOT_DISTURB_KEY = "notifications-do-not-disturb";

/**
 * Notifications controller
 */
export class NotificationsController {
    // Count of unread notifications
    public static UnreadNotificationsCount = 0;
    public static DoNotDisturb = false;

    private static AlertUser = "";
    private static AlertSession = "";
    private static AlertRequestVersion = 0;
    private static CheckingAlerts = false;
    private static AlertTimestamp: number | null = null;
    private static AlertIds = new Set<string>();

    private static SaveDoNotDisturb() {
        try {
            if (NotificationsController.AlertUser) {
                sessionStorage.setItem(
                    DO_NOT_DISTURB_KEY,
                    JSON.stringify({ user: NotificationsController.AlertUser, enabled: NotificationsController.DoNotDisturb }),
                );
            } else {
                sessionStorage.removeItem(DO_NOT_DISTURB_KEY);
            }
        } catch {
            // The preference still works in memory when storage is unavailable.
        }
    }

    public static SetDoNotDisturb(enabled: boolean) {
        if (NotificationsController.DoNotDisturb === enabled) return;
        NotificationsController.DoNotDisturb = enabled;
        NotificationsController.SaveDoNotDisturb();
        AppEvents.Emit("notification-do-not-disturb", enabled);
        if (enabled) {
            AppEvents.Emit("notification-alerts-clear");
        } else {
            // Start afresh so notifications received during the pause cannot be replayed.
            NotificationsController.ResetAlertBaseline();
            NotificationsController.CheckAlerts();
        }
    }

    private static ResetAlertBaseline() {
        NotificationsController.AlertRequestVersion++;
        Request.Abort(ALERT_NOTIFICATIONS_REQ_ID);
        NotificationsController.CheckingAlerts = false;
        NotificationsController.AlertTimestamp = null;
        NotificationsController.AlertIds.clear();
    }

    private static OnAuthChanged() {
        // Preserve the saved preference during startup, but always clear an established session on logout.
        if (AuthController.Loading && !AuthController.FirstTimeLoaded && !NotificationsController.AlertUser) return;
        const user = AuthController.isAuthenticated() && AuthController.hasPermission("notification.read") ? AuthController.UID : "";
        const session = user ? AuthController.Session : "";
        if (!user || user !== NotificationsController.AlertUser || session !== NotificationsController.AlertSession) {
            const firstSession = !NotificationsController.AlertSession;
            NotificationsController.ResetAlertBaseline();
            NotificationsController.AlertUser = user;
            NotificationsController.AlertSession = session;
            NotificationsController.DoNotDisturb = false;
            if (user && firstSession) {
                try {
                    const saved = JSON.parse(sessionStorage.getItem(DO_NOT_DISTURB_KEY) || "null");
                    NotificationsController.DoNotDisturb = saved?.user === user && saved.enabled === true;
                } catch {
                    // Ignore unavailable storage or an invalid saved preference.
                }
            }
            NotificationsController.SaveDoNotDisturb();
            AppEvents.Emit("notification-alerts-clear");
            AppEvents.Emit("notification-do-not-disturb", NotificationsController.DoNotDisturb);
        }
        NotificationsController.CheckNotifications();
    }

    private static CheckAlerts() {
        if (!NotificationsController.AlertUser || NotificationsController.CheckingAlerts) return;
        NotificationsController.CheckingAlerts = true;
        const version = ++NotificationsController.AlertRequestVersion;
        const session = NotificationsController.AlertSession;
        const muted = NotificationsController.DoNotDisturb;
        const isCurrent = () =>
            version === NotificationsController.AlertRequestVersion &&
            session === AuthController.Session &&
            AuthController.isAuthenticated() &&
            AuthController.hasPermission("notification.read");
        const timestamp = NotificationsController.AlertTimestamp;
        const notifications: NotificationItem[] = [];
        const loadPage = (continuation?: number) => {
            Request.Pending(
                ALERT_NOTIFICATIONS_REQ_ID,
                ApiNotifications.ListNotifications({ limit: 256, filterDateFrom: timestamp ?? undefined, continuation }),
            )
                .onSuccess((response) => {
                    if (!isCurrent()) return;
                    notifications.push(...response.list);
                    const next = response.continuationToken;
                    if (timestamp !== null && next && next >= timestamp && (continuation === undefined || next < continuation)) {
                        loadPage(next);
                        return;
                    }
                    NotificationsController.CheckingAlerts = false;
                    const fresh = notifications.filter(
                        (item) =>
                            timestamp !== null &&
                            (item.timestamp > timestamp ||
                                (item.timestamp === timestamp && !NotificationsController.AlertIds.has(item.id))),
                    );
                    const newest = notifications.reduce((latest, item) => Math.max(latest, item.timestamp), timestamp ?? 0);
                    if (timestamp !== newest) NotificationsController.AlertIds.clear();
                    NotificationsController.AlertTimestamp = newest;
                    for (const item of notifications) {
                        if (item.timestamp === newest) NotificationsController.AlertIds.add(item.id);
                    }
                    const unread = fresh.filter((item: NotificationItem) => !item.read);
                    if (!muted && !NotificationsController.DoNotDisturb && unread.length) {
                        AppEvents.Emit("notification-alerts", unread);
                    }
                })
                .onRequestError((err, handleErr) => {
                    if (!isCurrent()) return;
                    NotificationsController.CheckingAlerts = false;
                    handleErr(err, {
                        unauthorized: () => AppEvents.Emit("unauthorized"),
                        forbidden: () => {
                            NotificationsController.ResetAlertBaseline();
                            AppEvents.Emit("notification-alerts-clear");
                        },
                        temporalError: () => {}, // Retry with the next regular poll.
                    });
                })
                .onUnexpectedError((err) => {
                    if (!isCurrent()) return;
                    NotificationsController.CheckingAlerts = false;
                    console.error(err);
                });
        };
        loadPage();
    }

    public static Initialize() {
        AppEvents.AddEventListener("auth-status-changed", NotificationsController.OnAuthChanged);
        setInterval(NotificationsController.CheckNotificationsInterval, 5000);

        NotificationsController.OnAuthChanged();
    }

    private static CheckingNotifications = false;
    private static CountRequestVersion = 0;

    public static CheckNotificationsInterval() {
        if (!NotificationsController.CheckingNotifications) {
            NotificationsController.CheckNotifications();
        }
    }

    public static CheckNotifications() {
        const version = ++NotificationsController.CountRequestVersion;
        const session = AuthController.Session;
        const isCurrent = () => version === NotificationsController.CountRequestVersion && session === AuthController.Session;
        Timeouts.Abort(CHECK_NOTIFICATIONS_REQ_ID);
        Request.Abort(CHECK_NOTIFICATIONS_REQ_ID);

        if (!AuthController.isAuthenticated() || !AuthController.hasPermission("notification.read")) {
            NotificationsController.CheckingNotifications = false;
            NotificationsController.UnreadNotificationsCount = 0;
            AppEvents.Emit("notification-count-update", NotificationsController.UnreadNotificationsCount);
            return;
        }

        NotificationsController.CheckingNotifications = true;
        NotificationsController.CheckAlerts();

        Request.Pending(CHECK_NOTIFICATIONS_REQ_ID, ApiNotifications.CountUnread())
            .onSuccess((response) => {
                if (!isCurrent()) return;
                NotificationsController.CheckingNotifications = false;
                NotificationsController.UnreadNotificationsCount = response.count;
                AppEvents.Emit("notification-count-update", NotificationsController.UnreadNotificationsCount);
            })
            .onRequestError((err, handleErr) => {
                if (!isCurrent()) return;
                handleErr(err, {
                    unauthorized: () => {
                        NotificationsController.CheckingNotifications = false;
                        NotificationsController.UnreadNotificationsCount = 0;
                        AppEvents.Emit("notification-count-update", NotificationsController.UnreadNotificationsCount);
                        NotificationsController.ResetAlertBaseline();
                        AppEvents.Emit("notification-alerts-clear");
                    },
                    forbidden: () => {
                        NotificationsController.CheckingNotifications = false;
                        NotificationsController.UnreadNotificationsCount = 0;
                        AppEvents.Emit("notification-count-update", NotificationsController.UnreadNotificationsCount);
                        NotificationsController.ResetAlertBaseline();
                        AppEvents.Emit("notification-alerts-clear");
                    },
                    temporalError: () => {
                        Timeouts.Set(CHECK_NOTIFICATIONS_REQ_ID, 1500, NotificationsController.CheckNotifications);
                    },
                });
            })
            .onUnexpectedError((err) => {
                if (!isCurrent()) return;
                console.error(err);
                Timeouts.Set(CHECK_NOTIFICATIONS_REQ_ID, 1500, NotificationsController.CheckNotifications);
            });
    }
}

NotificationsController.Initialize();
